import { useCallback, useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { get } from "lodash";

import { queryClient } from "App";
import {
  getCallsTableColumns,
  getCallsTableFilters,
} from "components/CallsTableV3/CallsTableColumnsAndFilters";
import { CALLS_TABLE_CONSTANTS } from "components/CallsTableV3/CallsTableV3";
import tagConstants from "constants/Tag";
import CallDispositionService from "services/CallDispositions";
import CampaignService from "services/Campaign";
import CampaignTargetCallService from "services/CampaignTargetCall";
import EntityService from "services/Entity";
import TagService from "services/Tag";
import TwilioPhoneNumberService from "services/TwilioPhoneNumber";
import UserService from "services/User";
import { addResizedColumns } from "hooks/useTableConfig";
import { TableLink } from "components/BaseTableV3/TableLink";
import { formatUsersForFilter } from "helpers/user";
import { callKeys } from "components/CallsTableV3/queryKeys";

export const fetchInternalLines = async (filters = null) => {
  try {
    // The paginated table endpoint ignores search arguments, so the option
    // list has to come from the for_filters route to be searchable.
    const internalLinesData =
      await TwilioPhoneNumberService.getTwilioNumbersForFilters(filters);
    const internalLines = internalLinesData.map(({ id, name }) => ({
      id,
      name,
      value: id,
    }));
    return internalLines;
  } catch (error) {
    console.error("Error fetching internal lines:", error.message);
    return [];
  }
};

export const fetchNumbers = async (filters = null) => {
  try {
    const numberData = await CampaignTargetCallService.getNumberInfo(filters);
    if (numberData) {
      const contactNumbers = numberData.map(({ id, name }) => ({
        id,
        name,
        value: id,
      }));
      return contactNumbers;
    }
    return [];
  } catch (error) {
    console.error("Error fetching numbers:", error.message);
    return [];
  }
};

export const fetchStatuses = async () => {
  const callStatuses = await CallDispositionService.getDispositionsForFilters();
  return callStatuses;
};

const fetchAssignees = async () => {
  const rawUsers = await UserService.getUsers();
  return formatUsersForFilter(rawUsers);
};

const fetchTags = async () => {
  const taggableModels = tagConstants.taggableModels;
  const model = Object.keys(taggableModels).find(
    key => taggableModels[key] === CALLS_TABLE_CONSTANTS.className
  );

  const tags = await TagService.getTags({ model, for_filtering: true });
  return tags;
};

export const useCallsTable = ({ entityId, campaignId, contactId }) => {
  const { data: tableSettings, isSuccess: isTableSettingsFetched } = useQuery({
    queryKey: ["table-settings"],
    queryFn: UserService.getUserSettings,
  });

  const { data: tags, isSuccess: isTagsFetched } = useQuery({
    queryKey: ["tags", CALLS_TABLE_CONSTANTS.className],
    queryFn: fetchTags,
  });

  const { data: assignees, isSuccess: isAssigneesFetched } = useQuery({
    queryKey: ["assignees"],
    queryFn: fetchAssignees,
  });

  const { data: callStatuses, isSuccess: isCallStatusesFetched } = useQuery({
    queryKey: ["callStatuses"],
    queryFn: fetchStatuses,
  });

  const queryKey = useMemo(() => {
    if (entityId && contactId) return callKeys.list({ entityId, contactId });
    if (campaignId && entityId) return callKeys.list({ campaignId, entityId });
    if (campaignId) return callKeys.list({ campaignId });
    return callKeys.list();
  }, [campaignId, entityId, contactId]);

  const invalidateData = useCallback(async () => {
    await queryClient.invalidateQueries({ queryKey });
  }, [queryKey]);

  const filters = useMemo(() => {
    if (!isTagsFetched || !isAssigneesFetched) {
      return [];
    }
    const initialFilters = getCallsTableFilters({
      tags,
      assignees,
      campaignId,
    });

    if (!entityId) {
      initialFilters.splice(3, 0, {
        key: "entity_id",
        title: "Entity",
        type: "ruvixxSelect",
        queryFn: EntityService.getEntitiesForFilters,
        props: {
          campaign_ids: campaignId ? [campaignId] : [],
        },
      });
      initialFilters.splice(7, 0, {
        key: "assigned_user_id",
        title: "Assignee",
        type: "select",
        data: assignees,
      });
    }

    if (!campaignId) {
      initialFilters.splice(3, 0, {
        key: "campaign_id",
        title: "Campaign",
        type: "ruvixxSelect",
        queryFn: async (filters = {}) => {
          if (entityId) {
            filters["entity_id"] = entityId;
          }
          if (contactId) {
            filters["contact_id"] = contactId;
          }
          return await CampaignService.getCampaignsForFilters(filters);
        },
      });
    }
    return initialFilters;
  }, [
    tags,
    assignees,
    isTagsFetched,
    isAssigneesFetched,
    campaignId,
    entityId,
    contactId,
  ]);

  const columns = useMemo(() => {
    if (!isCallStatusesFetched || !isTableSettingsFetched) {
      return [];
    }
    const initialColumns = getCallsTableColumns({
      callStatuses,
      invalidateData,
    });
    const callsTableSettings = get(
      tableSettings,
      `user_settings.preferences.tables.${CALLS_TABLE_CONSTANTS.tableName}`,
      {}
    );
    const withResizedColumns = addResizedColumns({
      columns: initialColumns,
      resizedColumns: callsTableSettings?.resized_columns || {},
    });

    if (!campaignId) {
      const entityIndex = withResizedColumns.findIndex(
        column => column.id === "Entity"
      );
      if (entityIndex !== -1) {
        withResizedColumns.splice(entityIndex, 0, {
          header: "Campaign",
          id: "Campaign",
          accessorKey: "campaign_name",
          cell: ({ row }) => (
            <TableLink href={`/campaigns/${row.original.campaign_id}/calls`}>
              {row.original.campaign_name}
            </TableLink>
          ),
        });
      }
    }

    return withResizedColumns;
  }, [
    callStatuses,
    tableSettings,
    isTableSettingsFetched,
    isCallStatusesFetched,
    campaignId,
    invalidateData,
  ]);

  return { tags, filters, columns, queryKey, invalidateData };
};
